<template>
  <section class="widget-involved-person">
    <h3 :class="styles.control.label">{{ computedLabel }}</h3>
    <div class="widget-select-button">
      <WButton
        v-for="(label, index) in labels"
        :key="String(label)"
        :disabled="!control.enabled || involvedPersons?.length"
        :autofocus="appliedOptions.focus"
        :data-selected="value === values[index]"
        type="outline"
        :variant="getClientTheme()"
        @click="valueChanged(values[index])"
        >{{ label }}</WButton
      >
    </div>
    <div v-if="!!value" class="mt-8">
      <Transition name="fade" mode="out-in" appear>
        <ControlWrapper v-bind="controlWrapper" :styles="styles" :applied-options="appliedOptions">
          <SectionWrapper class="!mb-0">
            <template #header>
              <div></div>
            </template>
            <ActionCard
              v-if="!involvedPersons?.length"
              :title="personSection.cardTitle"
              :icon="personSection.icon"
              :content="personSection.cardContent"
              :disabled="!control.enabled"
              :action="`Add a ${personSection.type}`"
              @action="openPersonModal(-1)"
            />
            <div v-else class="flex h-full w-full flex-col gap-8">
              <div v-if="personSection.type === 'third party'" class="flex flex-row items-center">
                <p class="widget-involved-person_title">Third Parties</p>
                <WButton type="ghost" class="ml-auto" @click="openPersonModal(-1)">
                  <WidgetIcon :icon="themeIcons.plus" height="24" width="24" class="widget-action-icon" />
                </WButton>
              </div>
              <div
                v-for="(persons, groupIndex) in Object.entries(displayedPersons)"
                :key="groupIndex"
                class="flex h-full w-full flex-col"
              >
                <div class="mb-4 flex flex-row items-center">
                  <p class="widget-involved-person_subtitle">{{ capitalise(pluralize(persons[0])) }}</p>
                  <WButton
                    v-if="personSection.type === 'witness'"
                    type="ghost"
                    class="ml-auto"
                    @click="openPersonModal(-1)"
                  >
                    <WidgetIcon :icon="themeIcons.plus" height="24" width="24" class="widget-action-icon" />
                  </WButton>
                </div>
                <PersonCard
                  v-for="(person, index) in persons[1]"
                  :key="index"
                  :person="person"
                  class="mb-4 last:mb-0"
                  :edit-icon="personSection.editIcon"
                  :delete-icon="personSection.deleteIcon"
                  @edit="openPersonModal(index)"
                  @delete="deletePerson(index)"
                />
              </div>
            </div>
            <ThirdPartyOverlay
              v-if="isPersonModalOpen"
              v-model="isPersonModalOpen"
              :key="String(isPersonModalOpen)"
              :title="personSection.cardTitle"
              :type="personSection.type"
              :person="involvedPersons[selectedPersonIndex]"
              @submit="savePersons"
            />
          </SectionWrapper>
        </ControlWrapper>
      </Transition>
    </div>
  </section>
</template>

<script lang="ts">
  import { ref, defineComponent, computed } from 'vue';
  import { ControlElement } from '@jsonforms/core';

  import { rendererProps, useJsonFormsControl, RendererProps } from '@jsonforms/vue';
  import { useWidgetControl } from '../util';
  import type { PersonSection, ThirdPartyDriver } from 'src/types/overlays';
  import { capitalise } from 'src/utils/helpers';
  import { config, themeIcons, getClientTheme } from 'src/utils/config';
  import WidgetIcon from 'src/components/base/WidgetIcon.vue';

  import ActionCard from '../../base/ActionCard.vue';
  import ThirdPartyOverlay from '../../base/overlays/ThirdPartyOverlay.vue';
  import SectionWrapper from '../../claims-widget/components/SectionWrapper.vue';
  import ControlWrapper from './ControlWrapper.vue';
  import PersonCard from 'src/components/base/PersonCard.vue';
  import Button from 'src/components/base/Button.vue';

  type Key = 'witness' | 'driver' | 'motorcyclist' | 'pedestrian' | 'cyclist' | 'other';

  export default defineComponent({
    components: {
      PersonCard,
      ActionCard,
      ThirdPartyOverlay,
      SectionWrapper,
      ControlWrapper,
      WButton: Button,
      WidgetIcon,
    },
    props: {
      ...rendererProps<ControlElement>(),
    },
    setup(props: RendererProps<ControlElement>) {
      const isPersonModalOpen = ref(false);
      const control = useWidgetControl(useJsonFormsControl(props));
      const involvedPersons = ref<ThirdPartyDriver[]>([]);
      const value = ref();
      if (typeof control.control.value.data?.[0] === 'boolean') {
        value.value = control.control.value.data?.[0];
      }
      if (typeof control.control.value.data?.[0] === 'object') {
        involvedPersons.value = control.control.value.data;
        value.value = true;
      }
      const displayedPersons = computed(() =>
        involvedPersons.value.reduce(
          (acc, current) => {
            acc[current.type] = [...(acc[current.type] || []), current];
            return acc;
          },
          {} as Record<Key, ThirdPartyDriver[]>
        )
      );
      const selectedPersonIndex = ref<number>(-1);
      const personSection = ref(
        (control.control.value.schema as unknown as { personSection: PersonSection }).personSection
      );

      function openPersonModal(index = -1) {
        selectedPersonIndex.value = index;
        isPersonModalOpen.value = true;
      }
      function savePersons(persons: ThirdPartyDriver) {
        if (selectedPersonIndex.value !== -1) {
          involvedPersons.value.splice(selectedPersonIndex.value, 1, persons);
        } else {
          involvedPersons.value.push(persons);
        }
        isPersonModalOpen.value = false;
        control.onChange(involvedPersons.value);
      }

      function deletePerson(index: number) {
        involvedPersons.value.splice(index, 1);
        selectedPersonIndex.value = -1;
        control.onChange(involvedPersons.value);
      }

      function pluralize(word: string) {
        if (word === 'witness') {
          return 'witnesses';
        }
        return `${word}s`;
      }
      // yes no buttons

      if (
        !(
          'options' in control.control.value.schema &&
          typeof control.control.value.schema.options === 'object' &&
          Array.isArray(control.control.value.schema.options)
        )
      ) {
        throw new Error('must provide options param to option control');
      }
      const options = control.control.value.schema.options as [
        { title: string; value: string | number | boolean } | string | number | boolean,
      ];
      const labels = computed(() =>
        options.map((option) => {
          if (option && typeof option === 'object' && 'title' in option && 'value' in option) {
            return option.title;
          }
          return option;
        })
      );
      const values = computed(() =>
        options.map((option) => {
          if (option && typeof option === 'object' && 'title' in option && 'value' in option) {
            return option.value;
          }
          return option;
        })
      );

      function valueChanged(val: string | number | boolean | { title: string; value: string | number | boolean }) {
        control.onChange([val]);
        value.value = val;
      }

      return {
        isPersonModalOpen,
        involvedPersons,
        personSection,
        selectedPersonIndex,
        displayedPersons,
        labels,
        values,
        value,
        config,
        themeIcons,
        openPersonModal,
        savePersons,
        deletePerson,
        capitalise,
        pluralize,
        valueChanged,
        getClientTheme,
        ...control,
      };
    },
  });
</script>
